import React from "react";
import { Col, Form, Table } from "react-bootstrap";
import { Check, CheckAll, ExclamationTriangle, X } from "react-bootstrap-icons";
import { Article } from "../../models/responses/article";
import { ArticleType } from "../../models/enums/articleTypeEnum";
import { ArticleTemplate } from "../../models/responses/articleTemplate";

// ============================================
// INTERFACES
// ============================================

export interface EditableTimeTableHandlers {
  onArticleNumberChange: (rowNumber: number, value: string) => void;
  onArticleTemplateChange: (rowNumber: number, templateId: string) => void;
  onDescriptionChange: (rowNumber: number, value: string) => void;
  onQuantityChange: (rowNumber: number, value: number) => void;
  onRemove: (rowNumber: number) => void;
  onSalesPriceChange: (rowNumber: number, value: string) => void;
  onTimeChange: (rowNumber: number, value: number) => void;
  onToggleAllVerified: () => void;
  onValidatedChange: (rowNumber: number) => void;
}

interface EditableTimeTableProps {
  articles: Article[];
  allTimeVerified: boolean;
  isExported: boolean;
  articleTemplates: ArticleTemplate[];
  handlers: EditableTimeTableHandlers;
}

// ============================================
// COMPONENT
// ============================================

const EditableTimeTable: React.FC<EditableTimeTableProps> = ({
  articles,
  allTimeVerified,
  isExported,
  articleTemplates,
  handlers,
}) => {
  const timeArticles = articles.filter((a) => a.articleType === ArticleType.Time);

  if (timeArticles.length === 0 && !articles.some((a) => a.articleType === ArticleType.Time)) {
    // Only render if there are time articles or this is explicitly shown
  }

  return (
    <Col xs={12} md={12} className="mb-3">
      <h3>Reparationstider</h3>
      <Table responsive striped bordered hover>
        <thead>
          <tr>
            <th style={{ cursor: "pointer" }} onClick={handlers.onToggleAllVerified}>
              {allTimeVerified ? (
                <CheckAll className="text-success" />
              ) : (
                <ExclamationTriangle className="text-danger" />
              )}
            </th>
            <th>Från katalogen</th>
            <th>Välj mall</th>
            <th>Art.nummer</th>
            <th>Beskrivning</th>
            <th>Antal</th>
            <th>Försäljningspris</th>
            <th>Tid</th>
            <th>Ta bort</th>
          </tr>
        </thead>
        <tbody>
          {timeArticles.map((article) => (
            <tr key={article.rowNumber}>
              {/* Verified status */}
              <td>
                {!article.verified ? (
                  <X
                    className="text-danger"
                    onClick={() => handlers.onValidatedChange(article.rowNumber)}
                  />
                ) : (
                  <Check
                    className="text-success"
                    onClick={() => handlers.onValidatedChange(article.rowNumber)}
                  />
                )}
              </td>

              {/* External description (from catalog) */}
              <td>
                <Form.Control
                  type="text"
                  value={article.externalDescription}
                  size="sm"
                  readOnly
                  disabled
                />
              </td>

              {/* Template selector */}
              <td>
                <Form.Select
                  size="sm"
                  className="mt-1"
                  value={article.articleTemplateId || ""}
                  disabled={article.verified}
                  onChange={(e) => handlers.onArticleTemplateChange(article.rowNumber, e.target.value)}
                >
                  <option value="">- Välj mall -</option>
                  {articleTemplates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.articleNumber} - {template.description}
                    </option>
                  ))}
                </Form.Select>
              </td>

              {/* Article number */}
              <td>
                <Form.Control
                  type="text"
                  value={article.articleNumber}
                  size="sm"
                  onChange={(e) => handlers.onArticleNumberChange(article.rowNumber, e.target.value)}
                  readOnly={article.verified}
                  disabled={article.verified}
                />
              </td>

              {/* Description */}
              <td>
                <Form.Control
                  type="text"
                  value={article.description}
                  size="sm"
                  onChange={(e) => handlers.onDescriptionChange(article.rowNumber, e.target.value)}
                  readOnly={article.verified}
                  disabled={article.verified}
                />
              </td>

              {/* Quantity */}
              <td>
                <Form.Control
                  type="number"
                  value={article.quantity}
                  size="sm"
                  onChange={(e) => handlers.onQuantityChange(article.rowNumber, parseInt(e.target.value))}
                  readOnly={article.verified}
                  disabled={article.verified}
                />
              </td>

              {/* Sales price */}
              <td>
                <Form.Control
                  type="number"
                  value={article.salesPrice}
                  size="sm"
                  onChange={(e) => handlers.onSalesPriceChange(article.rowNumber, e.target.value)}
                  readOnly={article.verified}
                  disabled={article.verified}
                />
              </td>

              {/* Time */}
              <td>
                <Form.Control
                  type="number"
                  value={article.time}
                  size="sm"
                  onChange={(e) => handlers.onTimeChange(article.rowNumber, parseInt(e.target.value))}
                  readOnly={article.verified}
                  disabled={article.verified}
                />
              </td>

              {/* Remove button */}
              <td style={{ textAlign: "center" }}>
                <X
                  style={{ cursor: isExported ? "not-allowed" : "pointer" }}
                  className="text-danger"
                  size={20}
                  onClick={() => {
                    if (!isExported) {
                      handlers.onRemove(article.rowNumber);
                    }
                  }}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Col>
  );
};

export default EditableTimeTable;
